import { useEffect, useState, useCallback, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useToast } from "@/components/ui/use-toast";
import {
  ResourceFormData,
  resourceFormSchema,
} from "@/modules/hub/features/ResourceManagement/hooks/useCreateResource/types";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { getCreateUpdateResourceMetadataAction } from "@/modules/hub/features/ResourceManagement/slices/resourceManagementMetadataSlice";
import { createResourceAction } from "@/modules/hub/features/ResourceManagement/slices/createUpdateResourceSlice";
import { useNavigate } from "react-router-dom";
import { useNotification } from "@/context/NotificationContext";
import { slugify } from "@/utils/slugify";
import { ResourceTypes } from "@/modules/hub/features/ResourceManagement/hooks/useCreateResource/types";

export function useCreateResource() {
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { showNotification } = useNotification();
  const {
    resourceTags,
    resourceCategories,
    businessRoles,
    trades,
    resourceTypes,
    loading: metadataLoading,
    error: metadataError,
  } = useAppSelector((state) => state.resourceManagementMetadataReducer);

  useEffect(() => {
    dispatch(getCreateUpdateResourceMetadataAction());
  }, [dispatch]);

  const defaultValues: ResourceFormData = {
    title: "",
    slug: "",
    searchDescription: "",
    type: null,
    content_url: "",
    fileUuid: null,
    thumbnailFileUuid: null,
    publish_start_date: null,
    publish_end_date: null,
    is_published: false,
    requiresEaglesAccess: false,
    requiresLATAAccess: false,
    tags: [],
    categories: [],
    tradeIds: [],
    roleIds: [],
    contentBlocks: [],
    relatedResources: [],
    seriesEntries: [],
  };

  const form = useForm<ResourceFormData>({
    resolver: zodResolver(resourceFormSchema),
    defaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    const defaultType = resourceTypes.find((rt) => rt.isDefault === true);
    if (defaultType) {
      const currentType = form.getValues("type");
      if (currentType === null) {
        form.setValue("type", defaultType.id, { shouldDirty: false });
      }
    }
  }, [resourceTypes, form]);

  const resourceTypeId = form.watch("type");

  const selectedResourceType = useMemo(() => {
    if (!resourceTypeId) return null;
    return resourceTypes.find((rt) => rt.id === resourceTypeId) || null;
  }, [resourceTypeId, resourceTypes]);


  // Clear content blocks when Partner Page is selected
  useEffect(() => {
    if (selectedResourceType?.name === ResourceTypes.PARTNER_PAGE) {
      const currentBlocks = form.getValues("contentBlocks");
      if (currentBlocks && currentBlocks.length > 0) {
        form.setValue("contentBlocks", [], { shouldDirty: true });
      }
    }
  }, [selectedResourceType, form]);

  const submitForm = useCallback(
    async (values: ResourceFormData) => {
      try {
        setIsLoading(true);
        const numericType = values.type ?? 0;

        // Validation based on resource type
        const resourceTypeName = selectedResourceType?.name || "";

        // Validate required content_url for Video, Podcast, Partner Page
        if (
          [
            ResourceTypes.VIDEO,
            ResourceTypes.PODCAST,
            ResourceTypes.PARTNER_PAGE,
          ].includes(resourceTypeName as any)
        ) {
          if (!values.content_url) {
            toast({
              title: "Validation Error",
              description: `Content URL is required for ${resourceTypeName} resources`,
              variant: "destructive",
            });
            setIsLoading(false);
            return;
          }
        }

        // Validate required file for File type
        if (resourceTypeName === ResourceTypes.FILE && !values.fileUuid) {
          toast({
            title: "Validation Error",
            description: "File is required for File type resources",
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }

        // Clear content blocks for Partner Page
        const finalBlocks =
          resourceTypeName === ResourceTypes.PARTNER_PAGE
            ? []
            : (values.contentBlocks || []).map((b) => ({
                ...b,
                order_number: b.order_number ?? 0,
                fileUuid: b.fileUuid ?? null,
                // Transform shortDescription to short_description for API
                short_description: b.shortDescription || null,
                shortDescription: undefined, // Remove the camelCase version
                // Include tradeIds if they exist
                tradeIds: b.tradeIds || [],
              }));

        const publishStart = values.publish_start_date
          ? values.publish_start_date.toISOString()
          : null;
        const publishEnd = values.publish_end_date
          ? values.publish_end_date.toISOString()
          : null;
        const tagIds = (values.tags || []).map((t) => t.id);
        const categoryIds = (values.categories || []).map((c) => c.id);
        const relatedResourceIds = (values.relatedResources || []).map(
          (r) => r.id,
        );

        // Convert seriesEntries to seriesResourceIds for API
        const seriesResourceIds = (values.seriesEntries || []).map((s) => s.id);

        const finalData = {
          title: values.title,
          slug: values.slug ? slugify(values.slug) : null,
          searchDescription: values.searchDescription,
          type: numericType,
          content_url: values.content_url || null,
          fileUuid: values.fileUuid || null,
          thumbnailFileUuid: values.thumbnailFileUuid ?? null,
          publish_start_date: publishStart,
          publish_end_date: publishEnd,
          is_published: values.is_published ?? false,
          requiresEaglesAccess: values.requiresEaglesAccess ?? false,
          requiresLATAAccess: values.requiresLATAAccess ?? false,
          tagIds,
          categoryIds,
          relatedResourceIds,
          seriesResourceIds,
          tradeIds: values.tradeIds || [],
          roleIds: values.roleIds || [],
          contentBlocks: finalBlocks,
        };
        await dispatch(
          createResourceAction(finalData, () => {
            showNotification(
              "Success!",
              "Resource has been successfully created!",
              "success",
            );
            navigate(`/admin/resources`);
          }),
        );
      } catch (error) {
        console.error("Error saving resource:", error);
        if (error instanceof Error) {
          toast({
            title: "Error",
            description: error.message || "Failed to save",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: "An unknown error occurred while saving.",
            variant: "destructive",
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, toast, navigate, showNotification, selectedResourceType],
  );

  const handleCancel = useCallback(() => {
    navigate(`/admin/resources`);
  }, [navigate]);

  return {
    form,
    isLoading,
    tags: resourceTags,
    trades,
    categories: resourceCategories,
    roles: businessRoles,
    resourceTypes,
    metadataLoading,
    metadataError,
    resourceTypeId,
    selectedResourceType,
    submitForm,
    handleCancel,
  };
}
